/**
 * The browser features the engine cannot work without:
 * - `Worker` — the whole pipeline runs off the main thread.
 * - `createImageBitmap` — how previews, overlays and JPG/WebP decode work.
 * - `OffscreenCanvas` — how the worker rasterises those bitmaps.
 * `CompressionStream` is deliberately not here: PNG export falls back to fflate.
 */
export interface CapabilityGlobals { Worker?: unknown; createImageBitmap?: unknown; OffscreenCanvas?: unknown }

export function missingCapabilities(g: CapabilityGlobals): string[] {
  const missing: string[] = [];
  if (typeof g.Worker !== 'function') missing.push('Web Workers');
  if (typeof g.createImageBitmap !== 'function') missing.push('createImageBitmap');
  if (typeof g.OffscreenCanvas !== 'function') missing.push('OffscreenCanvas');
  return missing;
}

/** The banner text for an unsupported browser, or null when everything is present. */
export function capabilityMessage(missing: string[]): string | null {
  if (missing.length === 0) return null;
  const list = missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`;
  return `This browser is missing ${list}, which Easy Image Prep needs to process images. Use a current version of Chrome, Edge, Firefox or Safari.`;
}

/** Just enough of a global to probe WebGL2 with: the constructor the browser exposes when it knows
 *  the API at all, and a `document` to make a throw-away canvas on. A worker global has no
 *  `document`, which is why this is optional — and why the probe says no there. */
export interface Webgl2Globals {
  WebGL2RenderingContext?: unknown;
  document?: { createElement(tag: 'canvas'): { getContext(id: string): unknown } };
}

/**
 * Whether this global can actually hand out a WebGL2 context. The constructor alone is not enough:
 * a browser that knows the API still refuses a context on a blocklisted GPU, with the GPU process
 * dead, or with hardware acceleration off — so the probe has to ask for one. `getContext` is also
 * allowed to THROW in some of those states, hence the try/catch.
 *
 * Pure (the global is injected) so it can be tested in node, where neither name exists.
 */
export function webgl2Available(g: Webgl2Globals): boolean {
  try {
    return typeof g.WebGL2RenderingContext === 'function' && !!g.document?.createElement('canvas').getContext('webgl2');
  } catch {
    return false;
  }
}

let webgl2: boolean | undefined;
/** `webgl2Available(globalThis)`, asked once: the probe allocates a real context, and the answer
 *  cannot change without a page reload (a context LOST later is the renderer's problem, not this
 *  one's — see `MockupGl.onContextLost`). */
export function hasWebGL2(): boolean {
  if (webgl2 === undefined) webgl2 = webgl2Available(globalThis as unknown as Webgl2Globals);
  return webgl2;
}
